<template>
    <div class="flex flex-col gap-6">
        <div v-if="isLoading" class="body-small text-valence-grey-750">Loading profile...</div>
        <div v-else-if="error" class="body-small text-red-600">Error loading profile: {{ error }}</div>
        <div v-else-if="activeStateCard" class="rounded-2xl border border-valence-grey-200 bg-valence-grey-50 p-6">
            <div class="flex items-start gap-3">
                <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-white shadow-sm" :class="activeStateCard.iconColor">
                    <i :class="['bi', activeStateCard.icon, 'text-lg']" aria-hidden="true"></i>
                </div>
                <div class="flex flex-col gap-2">
                    <div class="title-body text-valence-grey-900">{{ activeStateCard.title }}</div>
                    <p class="body-small text-valence-grey-750">{{ activeStateCard.body }}</p>
                    <Link
                        v-if="activeStateCard.ctaText && activeStateCard.ctaHref"
                        class="body-small-bold text-valence-primary-600 hover:text-valence-primary-700 underline"
                        :href="activeStateCard.ctaHref">
                        {{ activeStateCard.ctaText }}
                    </Link>
                    <BaseButton v-else-if="activeStateCard.ctaText" theme="secondary" @click="emit('recreate')">
                        {{ activeStateCard.ctaText }}
                    </BaseButton>
                </div>
            </div>
        </div>
        <LoadingStep v-else-if="isGeneratingProfile" class="py-10 w-full" :main-loading-message="`Regenerating ${topicName}`" :has-perspective="hasPerspectiveReport" />
        <div v-else class="flex flex-col gap-3 @container">
            <p v-if="generationError" data-testid="topic-generation-error" class="body-small text-red-600 mt-3">{{ generationError }}</p>
            <div v-if="canRecreate && canRestore" class="flex flex-wrap items-center gap-3">
                <BaseButton theme="tertiary" class="flex items-center gap-1.5" :disabled="isRestoring" data-testid="restore-previous-profile" @click="handleRestorePrevious">
                    <i class="bi bi-arrow-counterclockwise text-sm/none" aria-hidden="true"></i>
                    {{ isRestoring ? "Restoring..." : "Restore previous profile" }}
                </BaseButton>
                <span v-if="restoreError" data-testid="topic-restore-error" class="body-small text-red-600">{{ restoreError }}</span>
            </div>
            <div class="flex gap-3 flex-col-reverse @2xl:flex-row">
                <div class="flex flex-col gap-3 grow">
                    <div
                        v-for="entry in entries"
                        :key="entry.id"
                        data-testid="topic-entry"
                        class="rounded-2xl border bg-white p-6 transition-shadow"
                        :class="editingId === entry.id ? 'border-valence-primary-400 ring-2 ring-valence-primary-500/20' : 'border-valence-grey-200'">
                        <div class="flex items-start gap-3">
                            <div class="flex shrink-0 items-center pt-0.5">
                                <component :is="entry.icon.component" :style="{ color: entry.icon.color }" class="h-5 w-5" />
                            </div>
                            <div class="flex min-w-0 flex-1 flex-col gap-2">
                                <div class="flex items-center justify-between gap-2">
                                    <!-- FIXME: when new typography classes are added, use it here -->
                                    <div class="title-subsection font-serif! text-valence-grey-900">{{ entry.title }}</div>
                                    <BaseButton
                                        v-if="editable && editingId !== entry.id"
                                        theme="neutral"
                                        size="sm"
                                        class="flex shrink-0 items-center gap-1.5"
                                        @click="startEditing(entry)">
                                        <i class="bi bi-pencil text-xs/none" aria-hidden="true"></i>
                                        Edit
                                    </BaseButton>
                                </div>
                                <!-- View mode -->
                                <template v-if="editingId !== entry.id">
                                    <p v-if="!entry.content" class="body-small italic text-valence-grey-400">{{ entry.placeholder }}</p>
                                    <template v-else-if="entry.lines.length > 1">
                                        <template v-for="(group, gi) in groupLines(entry.lines)" :key="gi">
                                            <ul
                                                v-if="group.isBullet"
                                                class="list-disc pl-5 body-small text-valence-grey-750 wrap-anywhere"
                                                :style="{ '--marker-color': entry.icon.markerColor ?? entry.icon.color }">
                                                <li v-for="(line, li) in group.items" :key="li" class="marker-colored">{{ line.text }}</li>
                                            </ul>
                                            <template v-else>
                                                <p v-for="(line, li) in group.items" :key="`p-${gi}-${li}`" class="body-small text-valence-grey-750 wrap-anywhere">
                                                    {{ line.text }}
                                                </p>
                                            </template>
                                        </template>
                                    </template>
                                    <p v-else class="body-small text-valence-grey-750 wrap-anywhere">{{ entry.content }}</p>
                                </template>
                                <!-- Edit mode -->
                                <template v-else>
                                    <Transition name="fade" mode="out-in">
                                        <BaseTextarea
                                            v-if="!isRefineMode"
                                            v-model:input="editContent"
                                            v-focus
                                            :aria-label="entry.title"
                                            class="w-full rounded-xl border border-valence-grey-300 p-3 body-small text-valence-grey-900 focus:border-valence-primary-500 focus:outline-none focus:ring-2 focus:ring-valence-primary-500/20 min-h-[4lh]"
                                            :placeholder="entry.placeholder" />
                                        <LoadingStep v-else-if="isRefining" hide-messages :main-loading-message="`Refining ${entry.title}`" class="mb-4" />
                                        <div v-else>
                                            <p class="body-small mb-2">Provide instructions for refining this profile topic section.</p>
                                            <VoiceTextarea
                                                v-model:input="refineInstruction"
                                                placeholder="Type or text instructions to refine this topic"
                                                :textarea-aria-label="`Refine profile topic entry for ${entry.title}`"
                                                textarea-class="body-small" />
                                        </div>
                                    </Transition>
                                    <Transition name="fade">
                                        <div v-if="!isRefining" class="flex items-center justify-between gap-3">
                                            <span v-if="displayedError" class="body-small text-red-600">{{ displayedError }}</span>
                                            <BaseButton v-if="!isRefineMode" theme="secondary" size="sm" :disabled="isSaving" @click="isRefineMode = true">
                                                <i class="bi bi-stars" aria-hidden="true"></i> Refine
                                            </BaseButton>
                                            <div class="flex items-center ml-auto gap-3">
                                                <BaseButton theme="neutral" size="sm" :disabled="isSaving" @click="cancelEditing"> Cancel </BaseButton>
                                                <BaseButton v-if="!isRefineMode" theme="primary" size="sm" :disabled="isSaving" @click="handleSave(entry)">
                                                    {{ isSaving ? "Saving..." : "Save" }}
                                                </BaseButton>
                                                <BaseButton v-else theme="primary" size="sm" :disabled="isRefining || !refineInstruction.trim()" @click="refineEntry">
                                                    Refine
                                                </BaseButton>
                                            </div>
                                        </div>
                                    </Transition>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
                <template v-if="editable && topicName">
                    <div class="flex flex-col gap-3 md:min-w-56">
                        <InferredDocuments :topic-name="topicName" :perspective-letters="perspectiveLetters" @add-source="isManageSourcesOpen = true" />
                        <slot name="sidebar"></slot>
                    </div>
                    <ManageSourcesDialog
                        v-model:open="isManageSourcesOpen"
                        :topic-name="topicName"
                        :perspective-letters="perspectiveLetters"
                        :free-text-enabled="freeTextEnabled"
                        @regenerate="handleRecreateTopic" />
                </template>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { Link } from "@inertiajs/vue3";
import { useProfileRefine } from "~vue/CollaborationProfile/composables/useProfileRefine.ts";
import InferredDocuments from "~vue/CollaborationProfile/InferredDocuments.vue";
import ManageSourcesDialog from "~vue/CollaborationProfile/ManageSourcesDialog.vue";
import { useProfileSources } from "~vue/composables/useProfileSources";
import { type ProfileSnapshot, useProfileUpdate } from "~vue/composables/useProfileUpdate";
import { useProfileWizard } from "~vue/composables/useProfileWizard";
import type { CollabIcon } from "~vue/utils/collabIcons";
import { computed, ref, watch } from "vue";

import BaseButton from "./BaseButton.vue";
import BaseTextarea from "./form/BaseTextarea.vue";
import VoiceTextarea from "./form/VoiceTextarea.vue";
import LoadingStep from "./ProfileWizard/LoadingStep.vue";

export interface ProfileLine {
    text: string;
    isBullet: boolean;
}

export interface ProfileEntry {
    id: string;
    title: string;
    content: string;
    lines: ProfileLine[];
    icon: CollabIcon;
    placeholder: string;
}

interface SectionState {
    title: string;
    body: string;
    icon: string;
    ctaText?: string;
    ctaHref?: string;
}

interface StateCardDisplay extends SectionState {
    iconColor: string;
}

const props = defineProps<{
    isLoading: boolean;
    error: string | null;
    hasProfile: boolean;
    entries: ProfileEntry[];
    emptyProfileState: SectionState;
    emptyEntriesState: SectionState;
    editable?: boolean;
    canRecreate?: boolean;
    perspectiveLetters?: string | null;
    isManager?: boolean;
    topicName?: string;
    hasPerspectiveReport?: boolean;
    freeTextEnabled?: boolean;
}>();

const emit = defineEmits<{
    saved: [id: string, content: string];
    recreate: [];
}>();

const vFocus = { mounted: (el: HTMLElement) => el?.focus() };

const editingId = ref<string | null>(null);
const editContent = ref<string>("");
const isSaving = ref(false);
const saveError = ref<string | null>(null);

function groupLines(lines: ProfileLine[]): { isBullet: boolean; items: ProfileLine[] }[] {
    const groups: { isBullet: boolean; items: ProfileLine[] }[] = [];
    for (const line of lines) {
        const last = groups[groups.length - 1];
        if (last && last.isBullet === line.isBullet) {
            last.items.push(line);
        } else {
            groups.push({ isBullet: line.isBullet, items: [line] });
        }
    }
    return groups;
}

const activeStateCard = computed((): StateCardDisplay | null => {
    if (!props.hasProfile) {
        return { ...props.emptyProfileState, iconColor: "text-valence-green-700" };
    }
    if (props.entries.length === 0) {
        return { ...props.emptyEntriesState, iconColor: "text-valence-green-700" };
    }
    return null;
});

const { saveSubtopic, canRestore, isRestoring, restoreError, takeSnapshot, restore } = useProfileUpdate(props.topicName ?? "");

function startEditing(entry: ProfileEntry): void {
    editingId.value = entry.id;
    editContent.value = entry.content;
    saveError.value = null;
}

function cancelEditing(): void {
    editingId.value = null;
    editContent.value = "";
    saveError.value = null;
    isRefineMode.value = false;
    refineInstruction.value = "";
}

async function handleSave(entry: ProfileEntry): Promise<void> {
    isSaving.value = true;
    saveError.value = null;
    const saved = await saveSubtopic(entry.id, editContent.value);
    isSaving.value = false;

    if (!saved) {
        saveError.value = "Failed to save. Please try again.";
    } else {
        const savedContent = editContent.value;
        editingId.value = null;
        editContent.value = "";
        emit("saved", entry.id, savedContent);
    }
}

const isManageSourcesOpen = ref<boolean>(false);

watch(isManageSourcesOpen, (newVal) => {
    if (newVal) {
        generationError.value = null;
        pendingSnapshot.value = {
            entries: props.entries.map(({ id, content }) => ({ id, content })),
            sourceIds: topicSources().map((s) => s.id),
        };
    }
});

const { generateProfile, generationError, isGeneratingProfile } = useProfileWizard({
    isManager: !!props.isManager,
    hasPerspectiveReport: !!props.hasPerspectiveReport,
});

const handleRecreateTopic = async (freeText?: string): Promise<void> => {
    if (isGeneratingProfile.value || isFetchingLinkedSources.value || fetchLinkedSourcesError.value || !props.canRecreate || !props.topicName) {
        return;
    }
    generationError.value = null;
    try {
        const fileIds = topicSources().map((s) => s.file_id);
        if (fileIds.length === 0 && !props.hasPerspectiveReport && !freeText) {
            generationError.value = "There are no sources to base your collaboration profile generation from. Edit sources to upload new documents.";
            return;
        }
        const previous = pendingSnapshot.value;
        await generateProfile({
            fileIds: fileIds,
            topicsFilter: [props.topicName],
            isRecreating: true,
            freeText,
        });
        if (generationError.value) {
            generationError.value = "Failed to regenerate. Please try again.";
            return;
        }
        if (previous) {
            takeSnapshot(previous.entries, previous.sourceIds);
        }
        await fetchLinkedSources();
        emit("recreate");
    } catch {
        generationError.value = "Failed to regenerate. Please try again.";
    }
};

const { fetchLinkedSources, linkedSources, isFetchingLinkedSources, fetchLinkedSourcesError } = useProfileSources({ fetchOnMount: !!props.editable });

const topicSources = () => (linkedSources.value?.sources ?? []).filter((s) => s.topic === props.topicName);

// Captured when the sources dialog opens, since sources linked there are already
// persisted by the time regeneration runs and would otherwise survive an undo.
const pendingSnapshot = ref<ProfileSnapshot | null>(null);

const handleRestorePrevious = async (): Promise<void> => {
    if (await restore(topicSources().map((s) => s.id))) {
        await fetchLinkedSources();
        emit("recreate");
    }
};

const { isRefining, refinementError, refinementData, handleRefine } = useProfileRefine();

const isRefineMode = ref<boolean>(false);
const refineInstruction = ref<string>("");

const displayedError = computed<string | null>(() => {
    if (isRefineMode.value && refinementError.value) {
        return "Failed to refine. Please try again.";
    }
    if (!isRefineMode.value && saveError.value) {
        return saveError.value;
    }
    return null;
});

const refineEntry = async (): Promise<void> => {
    const instruction = refineInstruction.value.trim();
    if (!instruction || isRefining.value) {
        return;
    }

    await handleRefine({
        currentContent: editContent.value,
        instruction,
    });

    if (refinementError.value) {
        return;
    }

    const refined = refinementData.value?.refined_content;
    if (refined) {
        editContent.value = refined;
    }
    refineInstruction.value = "";
    isRefineMode.value = false;
};
</script>

<style scoped>
.marker-colored::marker {
    color: var(--marker-color, currentColor);
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 150ms;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
